import React, { useEffect, useState, useRef, useCallback, useLayoutEffect } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Bell, X, BellRing, CheckCheck, Trash2 } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

import { formatDateTime } from '../lib/dateFormat';

const formatRel = (iso) => {
  try {
    const d = new Date(iso);
    const diff = (Date.now() - d.getTime()) / 1000;
    if (diff < 60) return 'just now';
    if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
    if (diff < 86400 * 7) return `${Math.floor(diff / 86400)}d ago`;
    return formatDateTime(d);
  } catch {
    return '';
  }
};

/**
 * NotificationsBell — bell icon with unread badge and dropdown list.
 *
 * Props:
 *   role: 'customer' | 'driver' | 'admin'
 *   authHeaders: object with the bearer token
 *   variant: 'light' (white nav) | 'dark' (driver portal) | 'admin' (sidebar)
 */
const NotificationsBell = ({ role, authHeaders, variant = 'light' }) => {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [panelPos, setPanelPos] = useState({ top: 0, right: 0 });
  const [flashing, setFlashing] = useState(false);
  const rootRef = useRef(null);
  const btnRef = useRef(null);
  const panelRef = useRef(null);
  const prevUnreadRef = useRef(null); // tracks previous unread count for "new arrival" detection
  const audioCtxRef = useRef(null);

  const basePath = role === 'driver' ? 'driver' : role === 'admin' ? 'admin' : 'customer';
  // Feb 2026 — Floor variant scopes the tray to notifications
  // explicitly targeted at the calling admin (task-assigned, stage-
  // complete). Broadcast module chatter (stock alerts, dispatch
  // updates) is filtered out server-side so warehouse workers only
  // see items relevant to their own queue.
  const scope = variant === 'floor' ? 'personal' : 'all';

  // Play a louder, more attention-grabbing chime for drivers; gentle 2-tone
  // chime for admin/customer.
  const playChime = useCallback(() => {
    try {
      const AC = window.AudioContext || window.webkitAudioContext;
      if (!AC) return;
      if (!audioCtxRef.current) audioCtxRef.current = new AC();
      const ctx = audioCtxRef.current;
      if (ctx.state === 'suspended') ctx.resume().catch(() => {});
      const now = ctx.currentTime;

      if (role === 'driver' || variant === 'floor') {
        // Driver + Warehouse-floor alert — louder, longer, four-tone
        // urgent pattern with overdrive. Floor workers are usually
        // wearing gloves and on the far side of noisy racks, so we
        // borrow the driver-grade chime instead of the gentle 2-tone.
        const pattern = [
          { freq: 1318, dur: 0.18 }, // E6
          { freq: 1760, dur: 0.18 }, // A6
          { freq: 1318, dur: 0.18 }, // E6
          { freq: 1760, dur: 0.32 }, // A6 — sustained tail
        ];
        let offset = 0;
        const masterGain = ctx.createGain();
        masterGain.gain.value = 0.85; // overall loudness boost
        masterGain.connect(ctx.destination);
        pattern.forEach(({ freq, dur }) => {
          // Use TWO oscillators per beat (sine + square) for richer, louder tone
          [
            { type: 'sine', g: 0.55 },
            { type: 'square', g: 0.18 },
          ].forEach(({ type, g }) => {
            const osc = ctx.createOscillator();
            const gain = ctx.createGain();
            osc.type = type;
            osc.frequency.setValueAtTime(freq, now + offset);
            gain.gain.setValueAtTime(0, now + offset);
            gain.gain.linearRampToValueAtTime(g, now + offset + 0.02);
            gain.gain.exponentialRampToValueAtTime(0.001, now + offset + dur);
            osc.connect(gain).connect(masterGain);
            osc.start(now + offset);
            osc.stop(now + offset + dur + 0.05);
          });
          offset += dur + 0.05;
        });
      } else {
        // Default 2-tone chime for admin/customer
        const tones = [880, 1320];
        tones.forEach((freq, i) => {
          const osc = ctx.createOscillator();
          const gain = ctx.createGain();
          osc.type = 'sine';
          osc.frequency.setValueAtTime(freq, now + i * 0.13);
          gain.gain.setValueAtTime(0, now + i * 0.13);
          gain.gain.linearRampToValueAtTime(0.22, now + i * 0.13 + 0.02);
          gain.gain.exponentialRampToValueAtTime(0.0001, now + i * 0.13 + 0.28);
          osc.connect(gain).connect(ctx.destination);
          osc.start(now + i * 0.13);
          osc.stop(now + i * 0.13 + 0.3);
        });
      }
    } catch {
      // ignore — autoplay restrictions etc.
    }
  }, [role, variant]);

  const load = useCallback(async () => {
    if (!authHeaders) return;
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/${basePath}/notifications`, {
        headers: authHeaders,
        params: scope === 'personal' ? { scope } : undefined,
      });
      const nextUnread = data.unread || 0;
      const prev = prevUnreadRef.current;
      // Only fire effects on increase, never on first load
      if (prev !== null && nextUnread > prev) {
        playChime();
        setFlashing(true);
        setTimeout(() => setFlashing(false), 1600);
      }
      prevUnreadRef.current = nextUnread;
      setItems(data.items || []);
      setUnread(nextUnread);
    } catch {
      // ignore
    } finally {
      setLoading(false);
    }
  }, [authHeaders, basePath, playChime, scope]);

  // Initial load only — polling disabled per admin request.
  // Real-time updates still arrive via the Service Worker MR_PUSH listener
  // below, so admins keep seeing new notifications the moment they're sent
  // (the bell flashes + chimes + refetches). For environments where push
  // isn't enabled, the bell refreshes on click via `refreshNow` and on
  // every full page navigation.
  useEffect(() => {
    if (!authHeaders) return;
    load();
  }, [authHeaders, load]);

  // Service worker push listener — when the SW receives a push, it broadcasts
  // an `MR_PUSH` message to all open tabs. We ring the in-page chime + flash
  // the bell immediately, then refetch so the new item appears in the list.
  useEffect(() => {
    if (!authHeaders || !('serviceWorker' in navigator)) return undefined;
    const onMessage = (event) => {
      const d = event?.data || {};
      if (d.type !== 'MR_PUSH') return;
      playChime();
      setFlashing(true);
      setTimeout(() => setFlashing(false), 1600);
      // Refresh notifications list quickly so the panel reflects the new item
      load();
    };
    navigator.serviceWorker.addEventListener('message', onMessage);
    return () => navigator.serviceWorker.removeEventListener('message', onMessage);
  }, [authHeaders, load, playChime]);

  // Close on outside click
  useEffect(() => {
    const onClick = (e) => {
      const inRoot = rootRef.current && rootRef.current.contains(e.target);
      const inPanel = panelRef.current && panelRef.current.contains(e.target);
      if (!inRoot && !inPanel) setOpen(false);
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, []);

  // Compute panel position whenever it opens / window resizes / scrolls
  useLayoutEffect(() => {
    if (!open) return undefined;
    const reposition = () => {
      const btn = btnRef.current;
      if (!btn) return;
      const r = btn.getBoundingClientRect();
      const vw = window.innerWidth;
      // Floor variant — much wider tray so warehouse workers can
      // read task titles at arm's length without squinting. On small
      // phones we fall back to `vw - 16` so it still fits.
      let desired;
      if (variant === 'floor') {
        desired = vw < 520 ? Math.min(vw - 16, 480) : 520;
      } else {
        desired = vw < 420 ? Math.min(vw - 16, 360) : 380;
      }
      // If bell is on the left half of viewport, anchor by left edge (grow right).
      // Otherwise anchor by right edge so panel right-aligns to bell (grow left).
      const bellCenter = (r.left + r.right) / 2;
      if (bellCenter < vw / 2) {
        let left = r.left;
        if (left + desired > vw - 8) left = Math.max(8, vw - desired - 8);
        setPanelPos({ top: r.bottom + 8, left, right: 'auto', width: desired });
      } else {
        let right = Math.max(8, vw - r.right);
        if (right + desired > vw - 8) right = Math.max(8, vw - desired - 8);
        setPanelPos({ top: r.bottom + 8, left: 'auto', right, width: desired });
      }
    };
    reposition();
    window.addEventListener('resize', reposition);
    window.addEventListener('scroll', reposition, true);
    return () => {
      window.removeEventListener('resize', reposition);
      window.removeEventListener('scroll', reposition, true);
    };
  }, [open, variant]);

  const markAllRead = async () => {
    try {
      await axios.post(
        `${API}/${basePath}/notifications/mark-read`,
        scope === 'personal' ? { scope } : {},
        { headers: authHeaders },
      );
      setItems((arr) => arr.map((n) => ({ ...n, read: true })));
      setUnread(0);
    } catch { /* noop */ }
  };

  const clearAll = async () => {
    if (items.length === 0) return;
    if (!window.confirm('Clear all notifications? This will remove every item from your inbox.')) return;
    try {
      await axios.post(
        `${API}/${basePath}/notifications/clear`,
        scope === 'personal' ? { scope } : {},
        { headers: authHeaders },
      );
      setItems([]);
      setUnread(0);
    } catch { /* noop */ }
  };

  const clearOne = async (id, e) => {
    e?.stopPropagation();
    try {
      await axios.post(
        `${API}/${basePath}/notifications/clear`,
        scope === 'personal' ? { id, scope } : { id },
        { headers: authHeaders },
      );
    } catch { /* noop */ }
    setItems((arr) => {
      const removed = arr.find((n) => n.id === id);
      const next = arr.filter((n) => n.id !== id);
      if (removed && !removed.read) setUnread((u) => Math.max(0, u - 1));
      return next;
    });
  };

  const openItem = async (n) => {
    if (!n.read) {
      try {
        await axios.post(
          `${API}/${basePath}/notifications/mark-read`,
          scope === 'personal' ? { id: n.id, scope } : { id: n.id },
          { headers: authHeaders },
        );
      } catch {}
      setItems((arr) => arr.map((x) => (x.id === n.id ? { ...x, read: true } : x)));
      setUnread((u) => Math.max(0, u - 1));
    }
    setOpen(false);
    if (n.url) {
      if (/^https?:\/\//.test(n.url)) {
        window.location.href = n.url;
      } else {
        navigate(n.url);
      }
    }
  };

  // --- styling ---
  const triggerCls =
    variant === 'dark'
      ? 'p-2 rounded-full text-slate-300 hover:bg-white/10 transition relative'
      : variant === 'admin'
      ? 'p-2 rounded-full text-neutral-700 hover:bg-neutral-100 transition relative'
      : variant === 'floor'
      ? 'p-3 rounded-full text-white bg-white/10 hover:bg-white/20 transition relative min-w-[48px] min-h-[48px] flex items-center justify-center'
      : 'p-2 rounded-full text-[#0F172A] hover:bg-neutral-100 transition relative';

  const panelCls =
    variant === 'dark'
      ? 'bg-[#111827] text-white border border-white/10 rounded-2xl shadow-2xl overflow-hidden'
      : variant === 'floor'
      ? 'bg-white text-[#0F172A] border border-neutral-200 rounded-2xl shadow-2xl overflow-hidden'
      : 'bg-white text-[#0F172A] border border-neutral-200 rounded-2xl shadow-2xl overflow-hidden';

  const itemCls = (read) => {
    if (variant === 'dark') {
      return `block w-full text-left px-4 py-3 border-b border-white/5 hover:bg-white/5 transition ${
        read ? 'opacity-70' : 'bg-emerald-500/[0.06]'
      }`;
    }
    if (variant === 'floor') {
      // Larger tap target (min ~72px), bigger fonts, extra breathing
      // room — reads well on a phone at arm's length.
      return `block w-full text-left px-5 py-4 border-b border-neutral-100 hover:bg-neutral-50 transition min-h-[72px] ${
        read ? 'opacity-60' : 'bg-amber-50/70'
      }`;
    }
    return `block w-full text-left px-4 py-3 border-b border-neutral-100 hover:bg-neutral-50 transition ${
      read ? 'opacity-70' : 'bg-blue-50/40'
    }`;
  };

  return (
    <div className="relative" ref={rootRef}>
      <button
        ref={btnRef}
        onClick={() => {
          // Refresh notifications on EVERY open so the tray is current
          // (polling is disabled, so this is the admin's manual refresh
          // path for environments without Web Push enabled).
          if (!open) load();
          setOpen((o) => !o);
        }}
        className={`${triggerCls} ${flashing ? 'notif-bell-flash' : ''}`}
        aria-label="Notifications"
        data-testid={`notif-bell-${role}`}
      >
        {unread > 0 ? (
          <BellRing className={`${variant === 'floor' ? 'w-6 h-6' : 'w-5 h-5'} ${flashing ? 'notif-bell-shake' : ''}`} />
        ) : (
          <Bell className={variant === 'floor' ? 'w-6 h-6' : 'w-5 h-5'} />
        )}
        {unread > 0 && (
          <span
            className={`absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 rounded-full bg-red-500 text-white text-[10px] font-bold flex items-center justify-center ring-2 ring-white ${
              flashing ? 'notif-badge-pulse' : ''
            }`}
            data-testid={`notif-unread-count-${role}`}
          >
            {unread > 99 ? '99+' : unread}
          </span>
        )}
      </button>

      {/* Local styles for flash + shake + badge pulse */}
      <style>{`
        @keyframes notif-flash {
          0%, 100% { box-shadow: 0 0 0 0 rgba(239, 68, 68, 0); }
          50%      { box-shadow: 0 0 0 8px rgba(239, 68, 68, 0.35); }
        }
        @keyframes notif-shake {
          0%, 100% { transform: rotate(0); }
          20% { transform: rotate(-14deg); }
          40% { transform: rotate(12deg); }
          60% { transform: rotate(-8deg); }
          80% { transform: rotate(6deg); }
        }
        @keyframes notif-badge-pulse {
          0%, 100% { transform: scale(1); }
          50% { transform: scale(1.35); }
        }
        .notif-bell-flash {
          animation: notif-flash 0.6s ease-out 2;
          border-radius: 9999px;
        }
        .notif-bell-shake {
          animation: notif-shake 0.8s ease-in-out;
          transform-origin: 50% 4px;
        }
        .notif-badge-pulse {
          animation: notif-badge-pulse 0.5s ease-in-out 2;
        }
        @media (prefers-reduced-motion: reduce) {
          .notif-bell-flash, .notif-bell-shake, .notif-badge-pulse { animation: none !important; }
        }
      `}</style>

      {open && createPortal(
        <div
          ref={panelRef}
          className={panelCls}
          style={{
            position: 'fixed',
            top: panelPos.top,
            left: panelPos.left,
            right: panelPos.right,
            width: panelPos.width,
            maxHeight: 'calc(100vh - 80px)',
            zIndex: 9999,
          }}
          data-testid={`notif-panel-${role}`}
        >
          <div
            className={`flex items-center justify-between px-4 py-3 border-b ${
              variant === 'dark' ? 'border-white/10' : 'border-neutral-100'
            }`}
          >
            <div className="flex items-center gap-2">
              <Bell className="w-4 h-4" />
              <h3 className="font-display font-bold">Notifications</h3>
              {unread > 0 && (
                <span
                  className={`text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full ${
                    variant === 'dark' ? 'bg-emerald-500/20 text-emerald-300' : 'bg-blue-100 text-blue-700'
                  }`}
                >
                  {unread} new
                </span>
              )}
            </div>
            <div className="flex items-center gap-1">
              {unread > 0 && (
                <button
                  onClick={markAllRead}
                  className={`text-xs font-semibold flex items-center gap-1 px-2 py-1 rounded-md ${
                    variant === 'dark'
                      ? 'text-emerald-300 hover:bg-white/5'
                      : 'text-[#0078D4] hover:bg-blue-50'
                  }`}
                  data-testid={`notif-mark-all-${role}`}
                >
                  <CheckCheck className="w-3.5 h-3.5" /> Mark all read
                </button>
              )}
              {items.length > 0 && (
                <button
                  onClick={clearAll}
                  className={`text-xs font-semibold flex items-center gap-1 px-2 py-1 rounded-md ${
                    variant === 'dark'
                      ? 'text-red-300 hover:bg-white/5'
                      : 'text-red-600 hover:bg-red-50'
                  }`}
                  data-testid={`notif-clear-all-${role}`}
                  title="Clear all notifications"
                >
                  <Trash2 className="w-3.5 h-3.5" /> Clear
                </button>
              )}
              <button
                onClick={() => setOpen(false)}
                className={`p-1 rounded-md ${variant === 'dark' ? 'hover:bg-white/5' : 'hover:bg-neutral-100'}`}
                aria-label="Close"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          </div>

          <div className="overflow-y-auto" style={{ maxHeight: 'calc(100vh - 180px)' }} data-testid={`notif-list-${role}`}>
            {loading && items.length === 0 ? (
              <div className={`px-4 py-10 text-center text-sm ${variant === 'dark' ? 'text-slate-400' : 'text-neutral-500'}`}>
                Loading…
              </div>
            ) : items.length === 0 ? (
              <div className={`px-4 py-10 text-center ${variant === 'dark' ? 'text-slate-400' : 'text-neutral-500'}`}>
                <Bell className={`w-8 h-8 mx-auto mb-2 ${variant === 'dark' ? 'text-slate-600' : 'text-neutral-300'}`} />
                <p className="text-sm">No notifications yet.</p>
              </div>
            ) : (
              items.map((n) => (
                <div
                  key={n.id}
                  onClick={() => openItem(n)}
                  className={`${itemCls(n.read)} group relative cursor-pointer`}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      openItem(n);
                    }
                  }}
                  data-testid={`notif-item-${n.id}`}
                >
                  <div className="flex items-start gap-2">
                    {!n.read && (
                      <span
                        className={`${
                          variant === 'floor' ? 'w-2.5 h-2.5 mt-2' : 'w-2 h-2 mt-1.5'
                        } rounded-full flex-shrink-0 ${
                          variant === 'dark'
                            ? 'bg-emerald-400'
                            : variant === 'floor'
                            ? 'bg-amber-500'
                            : 'bg-[#0078D4]'
                        }`}
                      />
                    )}
                    <div className="flex-1 min-w-0">
                      <div
                        className={`font-semibold ${
                          variant === 'floor' ? 'text-base leading-snug pr-8' : 'text-sm truncate pr-6'
                        }`}
                      >
                        {n.title}
                      </div>
                      {n.body && (
                        <div
                          className={`${
                            variant === 'floor' ? 'text-sm mt-1' : 'text-xs mt-0.5 line-clamp-2'
                          } ${
                            variant === 'dark' ? 'text-slate-400' : 'text-neutral-600'
                          }`}
                        >
                          {n.body}
                        </div>
                      )}
                      <div
                        className={`${
                          variant === 'floor' ? 'text-xs mt-2' : 'text-[10px] mt-1'
                        } uppercase tracking-wider font-semibold ${
                          variant === 'dark' ? 'text-slate-500' : 'text-neutral-400'
                        }`}
                      >
                        {formatRel(n.created_at)}
                      </div>
                    </div>
                  </div>
                  <button
                    onClick={(e) => clearOne(n.id, e)}
                    className={`absolute ${
                      variant === 'floor' ? 'top-3 right-3 p-2' : 'top-2 right-2 p-1'
                    } rounded ${
                      variant === 'floor' ? 'opacity-100' : 'opacity-0 group-hover:opacity-100'
                    } transition-opacity ${
                      variant === 'dark'
                        ? 'text-slate-400 hover:text-red-300 hover:bg-white/5'
                        : 'text-neutral-400 hover:text-red-600 hover:bg-red-50'
                    }`}
                    title="Remove this notification"
                    aria-label="Remove notification"
                    data-testid={`notif-clear-${n.id}`}
                  >
                    <X className={variant === 'floor' ? 'w-4 h-4' : 'w-3.5 h-3.5'} />
                  </button>
                </div>
              ))
            )}
          </div>
        </div>,
        document.body,
      )}
    </div>
  );
};

export default NotificationsBell;
